import { useCallback, useEffect, useState } from 'react';
import { Alert, Loading, PageHeader, Stat } from '../App';
import { adminApi, errorMessage, money, type FinancialReport, type RevenueBucket } from '../api/admin';

export function FinancialPage() {
  const [report, setReport] = useState<FinancialReport | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [includeSimulated, setIncludeSimulated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      setReport(
        await adminApi.financialReport({
          from: from || undefined,
          to: to || undefined,
          includeSimulated: includeSimulated || undefined,
        }),
      );
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [from, to, includeSimulated]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <PageHeader
        title="Financial reports"
        description="Money actually collected. Renewals due are reported as a count, never as projected revenue."
      />

      <Alert message={error} />

      <div className="admin-card mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-6 col-md-3">
            <label className="form-label small mb-1" htmlFor="fin-from">
              From
            </label>
            <input
              id="fin-from"
              type="date"
              className="form-control form-control-sm"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label small mb-1" htmlFor="fin-to">
              To
            </label>
            <input
              id="fin-to"
              type="date"
              className="form-control form-control-sm"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="fin-simulated"
                checked={includeSimulated}
                onChange={(e) => setIncludeSimulated(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="fin-simulated">
                Include simulated development payments
              </label>
              <div className="form-text">
                Off by default. Simulated payments are not real money and must never appear in a revenue figure by
                accident.
              </div>
            </div>
          </div>
        </div>
      </div>

      {!report ? (
        <Loading rows={6} />
      ) : (
        <>
          {report.caveat && <div className="admin-simulated mb-3">{report.caveat}</div>}

          <div className="row g-2 mb-4">
            <div className="col-6 col-lg-3">
              <Stat value={money(report.grossCollections)} label="Gross collections (incl. tax)" />
            </div>
            <div className="col-6 col-lg-3">
              <Stat value={money(report.taxCollected)} label="Tax collected" />
            </div>
            <div className="col-6 col-lg-3">
              <Stat value={money(report.refundedAmount)} label="Refunded" />
            </div>
            <div className="col-6 col-lg-3">
              <Stat value={money(report.netRevenue)} label="Net revenue" tone="ok" />
            </div>
          </div>

          <div className="row g-2 mb-4">
            <div className="col-6 col-lg-3">
              <Stat value={report.successfulPaymentCount} label="Successful payments" />
            </div>
            <div className="col-6 col-lg-3">
              <Stat value={report.failedPaymentCount} label="Failed payments" />
            </div>
            <div className="col-6 col-lg-3">
              <Stat value={report.activeSubscriptions} label="Active subscriptions" />
            </div>
            <div className="col-6 col-lg-3">
              <Stat value={report.upcomingRenewals} label="Renewals due in 30 days" />
            </div>
          </div>

          <p className="small text-muted">
            &ldquo;Renewals due&rdquo; counts subscriptions reaching their expiry date. It is not revenue: none of
            those owners has paid yet, and some will not renew.
          </p>

          <div className="row g-3">
            <div className="col-12 col-lg-6">
              <BucketTable title="By category" buckets={report.byCategory} />
            </div>
            <div className="col-12 col-lg-6">
              <BucketTable title="By billing type" buckets={report.byBillingType} />
            </div>
          </div>

          {report.byMonth.length > 0 && (
            <div className="mt-3">
              <h2 className="h6 text-uppercase text-muted mb-2">By month</h2>
              <MonthlyChart buckets={report.byMonth} />
            </div>
          )}
        </>
      )}
    </>
  );
}

function BucketTable({ title, buckets }: { title: string; buckets: RevenueBucket[] }) {
  return (
    <div>
      <h2 className="h6 text-uppercase text-muted mb-2">{title}</h2>

      {buckets.length === 0 ? (
        <div className="admin-card text-muted">Nothing collected in this period.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Segment</th>
                <th scope="col">Payments</th>
                <th scope="col">Gross</th>
                <th scope="col">Net</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((bucket) => (
                <tr key={bucket.label}>
                  <td>{bucket.label}</td>
                  <td>{bucket.paymentCount}</td>
                  <td>{money(bucket.grossCollections)}</td>
                  <td>{money(bucket.netRevenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * A plain horizontal bar list rather than a charting library: the dataset is a handful of
 * months, and the exact figures matter more than the shape.
 */
function MonthlyChart({ buckets }: { buckets: RevenueBucket[] }) {
  const max = Math.max(...buckets.map((b) => b.grossCollections), 1);

  return (
    <div className="admin-card">
      {buckets.map((bucket) => (
        <div className="mb-2" key={bucket.label}>
          <div className="d-flex justify-content-between small">
            <span>{bucket.label}</span>
            <span className="text-muted">
              {money(bucket.grossCollections)} gross · {money(bucket.netRevenue)} net · {bucket.paymentCount} payments
            </span>
          </div>
          <div
            className="admin-bar"
            style={{ width: `${Math.max(2, (bucket.grossCollections / max) * 100)}%` }}
            role="img"
            aria-label={`${bucket.label}: ${money(bucket.grossCollections)} gross`}
          />
        </div>
      ))}
    </div>
  );
}
